import { useEffect, useRef, useSyncExternalStore } from 'react';
import { Platform } from 'react-native';
import { usePathname, useRouter } from 'expo-router';
import * as QuickActions from 'expo-quick-actions';
import {
  isHomeRoute,
  isRestorableRoute,
  isParentRoute,
  isChildRoute,
  saveLastRoute,
  readLastRoute,
} from '@/lib/lastRoute';

// Persists the last in-app route and restores it on cold start.
//
// On iOS/Android the OS frequently terminates the app while it sits in the
// background (more aggressively the more memory the app uses — e.g. long
// chats with lots of media, or when the user bounces to another heavy app
// like a browser or terminal). When the app relaunches, Expo Router /
// React Navigation has no built-in state persistence on native, so the user
// lands on the home screen ("new chat") instead of where they left off
// (e.g. a chat opened from inside a collection). This hook closes that gap
// by remembering the deepest route the user was on — plus the container it was
// opened from, so OS back / swipe returns there — and replacing the initial
// home screen with it on the next launch.
//
// Web is intentionally excluded: the browser URL already preserves the route.

const PATHNAME_SETTLE_ATTEMPTS = 20;
const PATHNAME_SETTLE_INTERVAL_MS = 50;

// Deferred call helper for the cold-start restore; the restore effect records
// the returned id and releases any owed timer from its teardown on unmount.
const defer = (fn, ms) => setTimeout(fn, ms);

// Module-level settle flag so home (and other screens) can skip autofocus
// until the cold-start restore has decided — otherwise the composer focuses,
// the keyboard rises, then restore navigates away and tears it down.
let coldStartRestoreSettled = Platform.OS === 'web';
const coldStartRestoreListeners = new Set();

function setColdStartRestoreSettled(value) {
  if (coldStartRestoreSettled === value) return;
  coldStartRestoreSettled = value;
  coldStartRestoreListeners.forEach((listener) => listener());
}

export function useIsColdStartRestoreSettled() {
  return useSyncExternalStore(
    (onStoreChange) => {
      coldStartRestoreListeners.add(onStoreChange);
      return () => coldStartRestoreListeners.delete(onStoreChange);
    },
    () => coldStartRestoreSettled,
    () => true,
  );
}

export default function useRestoreLastRoute(enabled = true) {
  const pathname = usePathname();
  const router = useRouter();

  // Always-current pathname so the async restore read can inspect the latest
  // value without re-subscribing.
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  // The immediately-preceding route, used to attribute a parent (e.g. a
  // collection) to a chat opened directly from it.
  const prevPathRef = useRef(pathname);

  // Guards: run the cold-start restore at most once per mount, and don't start
  // persisting until that restore decision has been made (otherwise the first
  // persist of the home route would clobber the saved deep route before we get
  // a chance to read it).
  const didRunRestore = useRef(false);
  const restoreSettled = useRef(false);

  // Aborts the in-flight restore on unmount only. The restore effect also
  // re-runs whenever `enabled` flips, and auth churns during startup —
  // `token && !isTokenExpired()` briefly goes falsy while a token refreshes.
  // A per-run cancelled flag turned that cleanup into a silent abort: the
  // storage read would resolve after the flag was set and bail, and because
  // `didRunRestore` had already latched, the re-run did nothing. The user
  // landed on home with their last route still sitting in storage.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Timer owed by the cold-start restore, kept on a ref so an early-returning
  // effect re-run's teardown can still release a previous run's timer. At most
  // one is pending at a time: the settle loop awaits each delay before
  // creating the next, and the 400ms settle-flag timer starts after the loop.
  const restoreTimer = useRef(null);

  // Ignoring the auth churn above means we can no longer treat a cleanup as
  // "stop". Re-read auth at navigation time instead, so a real sign-out during
  // the restore doesn't drop the next user into the previous one's chat.
  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const persist = (path) => {
    if (isHomeRoute(path)) {
      // User is back at home — clear so we don't restore a stale deep route.
      saveLastRoute(null);
      return;
    }
    if (!isRestorableRoute(path)) {
      // Auth/checkout routes (paywall, login, …) are never restore targets,
      // but passing through one shouldn't erase the user's saved place either.
      return;
    }
    // Attribute a parent only for chat-like child routes navigated to *directly*
    // from a container (collection / tag). This lets the restore rebuild a back
    // stack so OS back / swipe returns to that container, without mis-attributing
    // unrelated chats opened later from the drawer.
    const prev = prevPathRef.current;
    const parent =
      isChildRoute(path) && isParentRoute(prev) && prev !== path ? prev : null;
    saveLastRoute({ path, parent });
  };

  // Cold-start restore.
  useEffect(() => {
    if (Platform.OS === 'web') {
      restoreSettled.current = true;
      setColdStartRestoreSettled(true);
    // Wait for a persisted session before restoring. Do NOT mark settled
    // true while `enabled` is false — that would open home autofocus for a
    // tick before the real restore run starts and tears the keyboard down.
    } else if (enabled && !didRunRestore.current) {
      didRunRestore.current = true;
      setColdStartRestoreSettled(false);

      // Records each owed timer id so the unmount teardown can release it.
      const later = (fn, ms) => {
        restoreTimer.current = defer(() => {
          restoreTimer.current = null;
          fn();
        }, ms);
      };

      (async () => {
        let didRestoreNavigate = false;
        try {
          // If the app was cold-launched via a Home Screen quick action (e.g.
          // "New Chat"), honor that intent instead of restoring the last route.
          const launchedViaQuickAction = !!QuickActions.initial;
          if (launchedViaQuickAction) return;

          const saved = await readLastRoute();
          if (!isMounted.current || !saved) return;

          // `usePathname()` is empty for a tick or two while the navigator
          // mounts. That is "not ready yet", not "the user navigated away", so
          // give it a moment rather than treating it as a reason to give up.
          for (let i = 0; i < PATHNAME_SETTLE_ATTEMPTS; i += 1) {
            if (pathnameRef.current) break;
            await new Promise((resolve) => later(resolve, PATHNAME_SETTLE_INTERVAL_MS));
            if (!isMounted.current) return;
          }

          // Only restore if the user is still sitting on the initial home screen
          // (i.e. they haven't already navigated somewhere themselves).
          if (!isHomeRoute(pathnameRef.current)) return;

          const { path, parent } = saved;

          // Scroll-position restore on cold start is DISABLED. It was the source
          // of "I open the app and I'm in the middle of a long conversation
          // instead of at the bottom" — the saved anchor positioned the list at
          // wherever the user was last reading, and combined with iOS's
          // maintainVisibleContentPosition (now also removed) it fought every
          // attempt to scroll to the bottom on send. Restoring the *route* (which
          // conversation) is still valuable; restoring the *scroll position*
          // within it is not worth the viewport instability on long chats. The
          // user lands at the bottom of their last conversation, which is the
          // expected behavior for a messaging app.
          // const convId = conversationIdFromPath(path);
          // if (convId) {
          //   const scroll = await readLastChatScroll(convId);
          //   if (!cancelled && scroll?.anchorMessageId) {
          //     useUIStore.getState().requestScrollToMessage({
          //       conversationId: convId,
          //       messageId: scroll.anchorMessageId,
          //       viewPosition: 0,
          //       animated: false,
          //       pinAnchor: true,
          //     });
          //   }
          // }
          if (!isMounted.current || !enabledRef.current) return;

          didRestoreNavigate = true;
          if (parent && isParentRoute(parent) && parent !== path) {
            // Rebuild a minimal back stack: container underneath, chat on top.
            router.replace(parent);
            router.push(path);
          } else {
            router.replace(path);
          }
        } catch (_) {
          // ignore — fall back to the default initial route
        } finally {
          // Open the gate for the pathname-change effect to start persisting. We
          // deliberately don't persist here: right after a restore navigation the
          // pathname ref still points at home for a tick, and persisting it would
          // momentarily clear the record we just restored. The pathname effect
          // records the destination as soon as navigation lands.
          restoreSettled.current = true;
          if (!didRestoreNavigate) {
            // Staying on home — safe to autofocus the composer now.
            setColdStartRestoreSettled(true);
          } else {
            // Navigating away: delay the settle flag so home can't remount with
            // autofocus in the gap before the replace unmounts it (keyboard thrash).
            later(() => setColdStartRestoreSettled(true), 400);
          }
        }
      })();
    }

    // Release the owed timer ONLY on unmount. A re-run (auth churn flips
    // `enabled`) must not abort the in-flight restore — see the isMounted
    // comment above. React runs a component's effect cleanups in declaration
    // order on unmount, and the unmount-only effect above is declared first,
    // so isMounted.current is already false here on a real unmount.
    return () => {
      if (isMounted.current) return;
      clearTimeout(restoreTimer.current);
      restoreTimer.current = null;
    };
  }, [enabled, router]);

  // Persist on every route change once the cold-start restore has settled,
  // then advance the previous-route pointer used for parent attribution.
  useEffect(() => {
    if (!enabled || Platform.OS === 'web') {
      prevPathRef.current = pathname;
      return;
    }
    if (restoreSettled.current) {
      persist(pathname);
    }
    prevPathRef.current = pathname;
  }, [enabled, pathname]);
}
